import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import FullCalendar from "@fullcalendar/react";
import { DateSelectArg, EventInput } from "@fullcalendar/core";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import rrulePlugin from "@fullcalendar/rrule";
import dayjs from "dayjs";

import {
  Grid,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Slide,
} from "@mui/material";
import {
  LocalizationProvider,
  DateTimePicker,
  renderTimeViewClock,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import Button from "../../../components/Button/Button";
import Modal from "../../../components/Modal/Modal";
import DriverDetailModal from "../../../components/DriverDetailModal/DriverDetailModal";

import { userAuthState } from "../../../atom";
import {
  useCreateAvailabilitySlot,
  getUserDriverAvailabilities,
  useDeleteDriverAvailabilitySlot,
} from "../../../hooks/useDriverAvailability";
import { OrganizationDriverAvailabilitySlot } from "../../../types/availability";
import { Snackbar, Alert } from "@mui/material";



function convertToEvent(slot: OrganizationDriverAvailabilitySlot): EventInput {
  if (!slot.recurrence || !slot.isRecurring) {
    return {
      id: slot.id,
      title: `${slot.user?.firstName ?? "Driver"} ${slot.user?.lastName ?? ""}`,
      start: slot.startTime,
      end: slot.endTime,
    };
  }

  const { frequency, interval, count, endDate } = slot.recurrence;

  return {
    id: slot.id,
    title: `${slot.user?.firstName ?? "Driver"} ${slot.user?.lastName ?? ""}`,
    rrule: {
      freq: frequency.toLowerCase(),
      dtstart: slot.startTime,
      interval: interval || 1,
      ...(count ? { count } : {}),
      ...(endDate ? { until: endDate } : {}),
    },
    duration:
      dayjs(slot.endTime).diff(dayjs(slot.startTime), "minute") + "m",
  };
}



const AvailabilityTab = ({
  userId,
  organizationId,
}: {
  userId: string;
  organizationId: string;
}) => {
  const auth = useRecoilValue(userAuthState);
  const queryClient = useQueryClient();
  const [events, setEvents] = useState<EventInput[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [isRecurring, setIsRecurring] = useState(false);
  const [frequency, setFrequency] = useState<"DAILY" | "WEEKLY" | "MONTHLY">("WEEKLY");
  const [selectedSlot, setSelectedSlot] = useState<OrganizationDriverAvailabilitySlot | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [startTimeError, setStartTimeError] = useState(false);
  const [endTimeError, setEndTimeError] = useState(false);
  const [startTouched, setStartTouched] = useState(false);
  const [endTouched, setEndTouched] = useState(false);

useEffect(() => {
  if (startTime) {
    const now = dayjs();
    const selectedStart = dayjs(startTime);
    const isBeforeToday = selectedStart.isBefore(now, 'day');
    setStartTimeError(isBeforeToday);
  }

  if (endTime && startTime) {
    const isEndBeforeStart = dayjs(endTime).isBefore(dayjs(startTime));
    setEndTimeError(isEndBeforeStart);
  }
}, [startTime, endTime]);


  const { data: availability = [] } = useQuery({
    queryKey: ["user-driver-availabilities", organizationId, userId],
    queryFn: () =>
      getUserDriverAvailabilities({
        accessToken: auth.accessToken,
        organizationId,
        userId,
      }),
    enabled: Boolean(auth.accessToken && organizationId && userId),
  });

  const createSlotMutation = useCreateAvailabilitySlot();

    useEffect(() => {
    if (availability.length) {
      setEvents(availability.map(convertToEvent));
    }
  }, [availability]);

  const resetForm = () => {
    setModalOpen(false);
    setStartTime(null);
    setEndTime(null);
    setIsRecurring(false);
  };

  const handleCreateSlot = async () => {
    if (!startTime || !endTime){
        setSnackbarMessage("Availability must have a start time and an end time.");
        setSnackbarOpen(true);
      return;}

    const now = dayjs();
    const start = dayjs(startTime);
    const end = dayjs(endTime);

    const startIsInvalid = !start || start.isBefore(now, 'day');

    if (startIsInvalid) {
      setSnackbarMessage("Start time must be today or in the future.");
      setSnackbarOpen(true);
      return;
    }

    if (!end.isAfter(start)) {
      setSnackbarMessage("End time must be after the start time.");
      setEndTimeError(true);
      setSnackbarOpen(true);
      return;
    }

    const payload = {
      accessToken: auth.accessToken,
      organizationId,
      userId,
      startTime,
      endTime,
      isRecurring,
      recurrence: isRecurring ? { frequency } : undefined,
    };

    try {
      await createSlotMutation.mutateAsync(payload);
      queryClient.invalidateQueries(["user-driver-availabilities", organizationId, userId]);

      setEvents((prev) => [
        ...prev,
        {
          title: isRecurring ? `Recurring Slot (${frequency})` : "New Slot",
          start: startTime.toISOString(),
          end: endTime.toISOString(),
        },
      ]);

      resetForm();
    } catch (err) {
      console.error("❌ Failed to create availability slot", err);
      setSnackbarMessage("Failed to create availability slot.");
      setSnackbarOpen(true);
    }
  };


  const handleDateSelect = (info: DateSelectArg) => {
    setStartTime(new Date(info.startStr));
    setEndTime(new Date(info.endStr));
    setModalOpen(true);
  };

  return (
    <><Grid sx={{ padding: 2 }} container direction="column" rowSpacing={2}>
      <Grid item>
        <Typography fontWeight={600} fontSize={16}>
          Driver Availability Calendar
        </Typography>
      </Grid>

      <Grid item>
        <Typography fontSize={14}>
          Select a time range on the calendar or click the button below to create a new availability slot.
        </Typography>
      </Grid>

      <Grid item>
        <Button label="Create Availability Slot" variant="primary" onClick={() => {
          const now = dayjs();
          setStartTime(now.toDate());
          setEndTime(now.add(1, "hour").toDate());
          setModalOpen(true);
        } }>Create Availability Slot</Button>
      </Grid>

      <Grid item>
        <FullCalendar
          plugins={[timeGridPlugin, interactionPlugin, rrulePlugin]}
          initialView="timeGridWeek"
          slotDuration="01:00:00"
          selectable
          select={handleDateSelect}
          editable={false}
          events={events}
          height="auto"
          eventClick={(info) => {
            const matchingSlot = availability.find((slot) => slot.id === info.event.id);

            if (matchingSlot) {
              setSelectedSlot(matchingSlot);
              setDetailsModalOpen(true);
            }
          } } />
      </Grid>



      <Modal
        open={modalOpen}
        handleClose={resetForm}
        onCancel={resetForm}
        onSubmit={handleCreateSlot}
        title="Add Availability Slot"
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <Typography fontWeight={500}>Is this slot recurring?</Typography>
              <RadioGroup
                row
                value={isRecurring ? "yes" : "no"}
                onChange={(e) => setIsRecurring(e.target.value === "yes")}
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>
            </Grid>

            {isRecurring && (
              <Grid item>
                <Typography fontWeight={500}>Recurrence</Typography>
                <RadioGroup
                  row
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value as typeof frequency)}
                >
                  <FormControlLabel value="DAILY" control={<Radio />} label="Daily" />
                  <FormControlLabel value="WEEKLY" control={<Radio />} label="Weekly" />
                  <FormControlLabel value="MONTHLY" control={<Radio />} label="Monthly" />
                </RadioGroup>
              </Grid>
            )}

           <Grid item>
              <DateTimePicker
                label="Start Time"
                value={startTime ? dayjs(startTime) : null}
                onChange={(date) => {
                  const newStart = date?.toDate() ?? null;
                  setStartTime(newStart);
                  setStartTouched(true);

                  if (newStart && (!endTime || dayjs(endTime).isBefore(newStart))) {
                    setEndTime(dayjs(newStart).add(1, "hour").toDate());
                  }
                }}
                onError={() => setStartTimeError(true)}
                viewRenderers={{ hours: renderTimeViewClock, minutes: renderTimeViewClock }}
                sx={{ width: "100%" }}
                slotProps={{
                  textField: {
                    error: startTimeError,
                    helperText: startTimeError ? "Start time must be in the future." : "",
                    fullWidth: true,
                    onBlur: () => setStartTouched(true),
                  },
                }}
              />
            </Grid>

            <Grid item>
              <DateTimePicker
                label="End Time"
                value={endTime ? dayjs(endTime) : null}
                onChange={(date) => {
                  setEndTime(date?.toDate() ?? null);
                  setEndTouched(true);
                }}
                onError={() => setEndTimeError(true)}
                viewRenderers={{ hours: renderTimeViewClock, minutes: renderTimeViewClock }}
                sx={{ width: "100%" }}
                slotProps={{
                  textField: {
                    error: endTimeError,
                    helperText: endTimeError ? "End time must be after start time." : "",
                    fullWidth: true,
                    onBlur: () => setEndTouched(true),
                  },
                }}
              />
            </Grid>

          </Grid>
        </LocalizationProvider>
      </Modal>

      {selectedSlot && (
        <DriverDetailModal
          accessToken={auth.accessToken}
          open={detailsModalOpen}
          onClose={() => setDetailsModalOpen(false)}
          user={selectedSlot.user}
          slot={selectedSlot} />
      )}

    </Grid>
    
    <Snackbar
      open={snackbarOpen}
      autoHideDuration={6000}
      onClose={() => setSnackbarOpen(false)}
      sx={{ zIndex: 1500 }}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      TransitionComponent={Slide}
    >
        <Alert onClose={() => setSnackbarOpen(false)} severity="error" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
    </Snackbar>
    
    </>
  );
};

export default AvailabilityTab;
